import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { deleteProjectSnapshot } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { Text, DeleteIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useDeleteSnapshotsBulkDialogContent } from "@pages/project-details/project-snapshots/bulk-actions/bulk-delete-snapshots-helper";
import { Snapshot } from "@custom-types/base-types";
import { isUserSnapshotAdmin } from "@utils/permission-control/snapshot-permission-control";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { assert } from "@faro-lotv/foundation";

/** Show delete snapshots button and dispatch action where the bulk dialog will act accordingly */
export function BulkDeleteSnapshots(): JSX.Element {
  const projectId = useAppSelector(selectedProjectIdSelector);

  assert(
    projectId,
    "BulkDeleteSnapshots: project context is undefined, but required in this case"
  );

  const dispatch = useAppDispatch();
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);

  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(
    selectedEntitiesSelector("snapshots")
  );

  const { bulkDialogContent } = useDeleteSnapshotsBulkDialogContent();
  const mode = useBulkActionsMode();

  // Closes the dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onDeleteSnapshotClick(): void {
    dispatch(setBulkActionName("deleteSnapshots"));

    for (const entity of selectedEntities) {
      const hasUserValidRole = isUserSnapshotAdmin(entity);

      if (!hasUserValidRole) {
        dispatch(
          updateOne({
            id: entity.id,
            changes: {
              status: "not-allowed",
              message:
                "You are not allowed to delete this snapshot from workspace",
            },
          })
        );
      }
    }

    setShouldShowDialog(true);
  }

  const config = getBulkActionButtonConfig(
    mode,
    "Delete snapshot from workspace"
  );

  return (
    <>
      <FaroTextButton
        onClick={onDeleteSnapshotClick}
        tooltipText={config.tooltipText}
        startIcon={<DeleteIcon width={18} height={18} />}
        rippleColor={config.rippleColor}
        sx={config.sx}
      >
        {config.shouldShowText && <Text variant="buttonM">Delete</Text>}
      </FaroTextButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "deleteSnapshots" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkDeleteSnapshots}
          bulkActionCallback={(snapshot: Snapshot) =>
            dispatch(
              deleteProjectSnapshot({
                projectId,
                snapshotId: snapshot.id,
              })
            ).unwrap()
          }
          selectedEntities={selectedEntities}
          uniqueIdKey="id"
          tableDataType="snapshots"
          dialogVariant="danger"
        />
      )}
    </>
  );
}
